import {
  Component, Input, OnChanges, OnInit, OnDestroy, ViewChild,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, AbstractControl, AsyncValidatorFn } from '@angular/forms';
import * as _ from 'lodash';
import { Observable } from 'rxjs';




import { TableComponent } from 'shared/table/table/table.component';
import { FormControlFilenameDirective } from 'shared/form/form-control-filename.directive';

import { digitalAssetErrors } from 'shared/digital-assets/digital-asset-errors';
import * as utils from 'utils/utils';
import { fieldsInfo } from './fields-info';
import { DigitalAsset } from './digital-asset.model';
import { isAssetItemShown } from 'shared/digital-assets/digital-asset-helpers';

import { DigitalAssetsService } from 'shared/digital-assets/digital-assets.service';
import { BrandService } from 'shared/brand/brand.service';
import {debounceTime, distinctUntilChanged, takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'digital-assets-form',
  templateUrl: 'digital-assets-form.component.html',
  styleUrls: ['digital-assets-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitalAssetsFormComponent extends TableComponent implements OnChanges, OnInit, OnDestroy {
  form: UntypedFormGroup;

  @Input() declare data: any;

  @Input() declare segmentData: any;

  @ViewChild(FormControlFilenameDirective, {static: false})
  filenameElement: FormControlFilenameDirective | void;

  private fieldsInfo = fieldsInfo;
  private isActive: boolean = true;
  private initialFilename: string;

  get brandId(): string {
    return this.brandService.getBrand();
  }

  constructor(
    private fb: UntypedFormBuilder,
    private digitalAssetsService: DigitalAssetsService,
    private brandService: BrandService,
    private ref: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnChanges() {
    this.setData(this.data);
    _.defer(() => this.ref.markForCheck());
  }

  ngOnInit() {
    this.form.get('filename')
    .valueChanges
    .pipe(distinctUntilChanged(), debounceTime(500), takeWhile(() => this.isActive))
    .subscribe((event) => {
      if (this.form.get('filename').pristine) { return; }
      this.checkFilenameAvailability(
        event.indexOf('.') < 0 && this.filenameElement ? `${event}.${this.filenameElement.fileType}` : event
      );
    });
  }

  ngOnDestroy() {
    this.isActive = false;
    this.data = {};
    this.fieldsInfo = [];
  }

  // handlers
  onDateChange(selectedDate: string, item: any) {
    if (item.item_name === 'expiration_date') {
      this.handleDateAheadChange(
        this.form.get('effectiveDate'),
        'effective_date',
        selectedDate
      );
    }
  }

  // private
  private setData(data: any = {}): void {
    if (this.form) {
      this.updateForm();
    } else {
      this.createForm();
    }
    this.initialFilename = data.filename;
  }

  private createForm(): void {
    const formControls = utils.populateModel(DigitalAsset, this.segmentData, this.data, this);
    this.form = this.fb.group(formControls);
  }

  private updateForm(): void {
    _.each(this.segmentData, (item: any) => {
      const control = this.form.get(item.field_map);
      if (control) { control.setValue(_.get(this.data, item.item_name, '')); }
    });
    this.setDateDisabledRange(this.form.get('expirationDate').value, 'effective_date');
    this.setDateAheadControlError(
      this.form.get('effectiveDate'),
      this.form.get('expirationDate')
    );
    _.defer(() => this.form.markAsPristine());
  }

  private isFieldShown(item: any): boolean {
    return isAssetItemShown(this.data, item);
  }

  private checkFilenameAvailability(value: string) {
    if (value === this.initialFilename) {
      this.form.get('filename').setErrors(null);
      return;
    }
    this.digitalAssetsService.getFilenameAvaliability(
      this.brandId,
      value
    ).subscribe((response: any) => {
      const filenameAvaliableErrors = response.available ? null : {'unique': digitalAssetErrors.filenameUniq};
      if (filenameAvaliableErrors) {
        const errors = this.form.get('filename').errors;
        this.form.get('filename').setErrors(_.merge(errors, filenameAvaliableErrors));
      }
    });
  }

  private showLabelWord(itemName: string): boolean {
    return itemName === 'resolution' || itemName === 'color_mode';
  }

  private getFieldValidation(item: any, form: any) {
    const data = this.fieldValidation(item, form);
    if (!data.length) { return []; }

    // PIES 6.7 validation row was added for test
    return [
      ...data,
      {
        key: 'pies67',
        title: 'PIES 6.7',
        valid: data[0].valid,
      }
    ];
  }
}
